// src/context/AuthContext.js
import React, { createContext, useState, useEffect, useContext } from 'react';
import { auth, db } from '../firebase/config'; // Updated path for LMS app
import { onAuthStateChanged, signOut, signInWithEmailAndPassword } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';

export const AuthContext = createContext();
export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState('');
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Enhanced login function to ensure proper role fetching
  const login = async (email, password) => {
    try {
      // First, fully clear previous state
      await signOut(auth);
      setCurrentUser(null);
      setUserRole('');
      setUserData(null);
      
      // Now perform new login
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      
      // Immediately fetch user data to ensure role is correct
      const userDoc = await getDoc(doc(db, "users", userCredential.user.uid));
      if (userDoc.exists()) {
        const data = userDoc.data();
        console.log("Login - User role:", data.role); // Debug log

        // Block archived users
        if (data.archived === true) {
          await signOut(auth);
          throw new Error("Your account has been deactivated. Please contact your administrator.");
        }
        
        // Set user data explicitly
        setCurrentUser(userCredential.user);
        setUserRole(data.role);
        setUserData(data);
        
        return {
          user: userCredential.user,
          userData: data
        };
      } else {
        throw new Error("User data not found in database");
      }
    } catch (error) {
      console.error("Login error:", error);
      throw error;
    }
  };

  // Improved logout function that properly clears all state
  const logout = async () => {
    console.log("Logging out - clearing state"); // Debug log
    
    try {
      // Clear all auth state before sign out
      setCurrentUser(null);
      setUserRole('');
      setUserData(null);
      
      // Then sign out from Firebase
      await signOut(auth);
      
      // Force reload to clear any cached state
      // This is important when switching between users with different roles
      window.location.href = '/login';
      
      return true;
    } catch (error) {
      console.error("Logout error:", error);
      throw error;
    }
  };

  // Add function to check if user has required role
  const hasRole = (requiredRole) => {
    console.log("Checking role:", requiredRole, "User has role:", userRole); // Debug log
    
    if (!userRole) return false;
    
    if (requiredRole === 'admin') {
      return userRole === 'admin';
    } else if (requiredRole === 'mentor') {
      return userRole === 'admin' || userRole === 'mentor';
    } else if (requiredRole === 'learner') {
      return userRole === 'admin' || userRole === 'mentor' || userRole === 'learner';
    } else if (requiredRole === 'official') {
      // Officials are a separate read-only reporting role, not part of the
      // learner < mentor < admin hierarchy — only admins and officials
      // themselves should pass this check. Mirrors the 'official' case
      // already present in getRedirectPath below.
      return userRole === 'admin' || userRole === 'official';
    }
    
    return false;
  };

  // Add this function to determine redirect paths based on user role
  const getRedirectPath = (role) => {
    console.log("Getting redirect path for role:", role); // Debug log
    
    switch (role) {
      case "admin":
        return "/admin";
      case "official":
        return "/admin"; // same route as admin — AdminDashboard branches on role internally
      case "mentor":
        return "/mentor";
      case "tutor":
        return "/tutor-dashboard";
      case "learner":
        return "/learn/dashboard"; // Updated for LMS app structure
      default:
        return "/login";
    }
  };

  useEffect(() => {
    console.log("Setting up auth state listener"); // Debug log
    
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log("Auth state changed:", user ? "User logged in" : "No user"); // Debug log
      
      if (user) {
        try {
          // Get user role from Firestore
          const userDocRef = doc(db, "users", user.uid);
          const userDoc = await getDoc(userDocRef);
          
          if (userDoc.exists()) {
            const data = userDoc.data();
            console.log("Auth state - User role:", data.role); // Debug log

            // Block archived users on page refresh / session restore
            if (data.archived === true) {
              setCurrentUser(null);
              setUserRole('');
              setUserData(null);
              await signOut(auth);
              setLoading(false);
              return;
            }
            
            setCurrentUser(user);
            setUserRole(data.role);
            setUserData(data);
          } else {
            console.error("User doc not found in Firestore");
            setCurrentUser(null);
            setUserRole('');
            setUserData(null);
            await signOut(auth);
          }
        } catch (error) {
          console.error("Error fetching user data:", error);
          setCurrentUser(null);
          setUserRole('');
          setUserData(null);
        }
      } else {
        console.error("User doc not found in Firestore");
        // Don't sign out — the user doc may be about to be written (e.g. during registration)
        // Just leave the auth session intact and set minimal state
        setCurrentUser(user);
        setUserRole('');
        setUserData(null);
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userRole,
    userData,
    login,
    logout,
    loading,
    getRedirectPath,
    hasRole
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

// hook for easier access
export function useAuth() {
  return useContext(AuthContext);
}